import { lessonId } from "./progress";
import type { CourseManifest, ChapterManifestEntry } from "./course";

export type CourseStatus = "not-started" | "in-progress" | "completed";

export interface CourseResumeInfo {
  slug: string;
  status: CourseStatus;
  lastVisitedTs: number | null;
  indexOrder: number;
}

export type ResumeTarget =
  | { kind: "resume"; slug: string }
  | { kind: "start"; slug: string }
  | { kind: "all-complete" }
  | { kind: "none" };

export function selectResumeTarget(courses: CourseResumeInfo[]): ResumeTarget {
  if (courses.length === 0) return { kind: "none" };
  const inProgress = courses.filter((c) => c.status === "in-progress");
  if (inProgress.length > 0) {
    const best = inProgress.reduce((a, b) => {
      const at = a.lastVisitedTs ?? -1, bt = b.lastVisitedTs ?? -1;
      if (bt > at) return b;
      if (bt < at) return a;
      return a.indexOrder <= b.indexOrder ? a : b;
    });
    return { kind: "resume", slug: best.slug };
  }
  const notStarted = courses
    .filter((c) => c.status === "not-started")
    .sort((a, b) => a.indexOrder - b.indexOrder);
  const [first] = notStarted;
  if (first) return { kind: "start", slug: first.slug };
  return { kind: "all-complete" };
}

export function courseMinutesRemaining(manifest: CourseManifest, completed: Set<string>): number {
  let sum = 0;
  for (const ch of manifest.chapters)
    for (const ls of ch.lessons)
      if (!completed.has(lessonId(ch.folder, ls.slug))) sum += ls.estimatedMinutes;
  return sum;
}

export function chapterMinutesRemaining(chapter: ChapterManifestEntry, completed: Set<string>): number {
  let sum = 0;
  for (const ls of chapter.lessons)
    if (!completed.has(lessonId(chapter.folder, ls.slug))) sum += ls.estimatedMinutes;
  return sum;
}

export function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
